let audioContext = null;
let audioUnlocked = false;
const htmlAudioCache = new Map();

const SOUND_TONES = {
  laser: { frequency: 520, endFrequency: 390, duration: 0.055, type: 'triangle', volume: 0.05 },
  explosion: { frequency: 160, endFrequency: 48, duration: 0.22, type: 'sawtooth', volume: 0.12 },
  damage: { frequency: 220, endFrequency: 90, duration: 0.18, type: 'triangle', volume: 0.1 },
  bossHit: { frequency: 260, endFrequency: 180, duration: 0.07, type: 'triangle', volume: 0.06 },
  levelClear: { frequency: 660, duration: 0.14, type: 'triangle', volume: 0.08 },
  victory: { frequency: 783.99, duration: 0.18, type: 'triangle', volume: 0.085 },
  bossIntro: { frequency: 174.61, duration: 0.18, type: 'sawtooth', volume: 0.07 },
  powerup: { frequency: 720, endFrequency: 980, duration: 0.1, type: 'triangle', volume: 0.06 },
};

export function resetArcadeAudioForTests() {
  audioContext = null;
  audioUnlocked = false;
  htmlAudioCache.clear();
}

function shouldUseHtmlAudioFallback() {
  const userAgent = window.navigator?.userAgent ?? '';
  const platform = window.navigator?.platform ?? '';
  const isTouchMac = platform === 'MacIntel' && (window.navigator?.maxTouchPoints ?? 0) > 1;

  return /iPad|iPhone|iPod/.test(userAgent) || isTouchMac;
}

function getAudioContext() {
  const AudioContextConstructor = window.AudioContext || window.webkitAudioContext;

  if (!AudioContextConstructor) {
    return null;
  }

  if (!audioContext) {
    audioContext = new AudioContextConstructor();
  }

  if (audioContext.state === 'suspended') {
    audioContext.resume()?.catch?.(() => {});
  }

  return audioContext;
}

export function unlockArcadeAudio() {
  const context = getAudioContext();

  if (shouldUseHtmlAudioFallback()) {
    unlockHtmlAudio();
  }

  if (!context || audioUnlocked) {
    return;
  }

  const oscillator = context.createOscillator();
  const gain = context.createGain();
  const now = context.currentTime;

  oscillator.type = 'triangle';
  oscillator.frequency.setValueAtTime(220, now);
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.025);

  oscillator.connect(gain);
  gain.connect(context.destination);
  oscillator.start(now);
  oscillator.stop(now + 0.025);
  audioUnlocked = true;
}

function getWaveValue(type, phase) {
  if (type === 'triangle') {
    return 2 * Math.abs(2 * (phase - Math.floor(phase + 0.5))) - 1;
  }

  if (type === 'sawtooth') {
    return 2 * (phase - Math.floor(phase + 0.5));
  }

  return Math.sin(phase * Math.PI * 2);
}

function encodeWavDataUrl({ frequency, endFrequency, duration, type = 'triangle', volume = 0.08 }) {
  const sampleRate = 22050;
  const sampleCount = Math.max(1, Math.floor(sampleRate * duration));
  const buffer = new ArrayBuffer(44 + sampleCount * 2);
  const view = new DataView(buffer);
  let offset = 0;

  const writeString = (value) => {
    for (let index = 0; index < value.length; index += 1) {
      view.setUint8(offset, value.charCodeAt(index));
      offset += 1;
    }
  };

  writeString('RIFF');
  view.setUint32(offset, 36 + sampleCount * 2, true);
  offset += 4;
  writeString('WAVE');
  writeString('fmt ');
  view.setUint32(offset, 16, true);
  offset += 4;
  view.setUint16(offset, 1, true);
  offset += 2;
  view.setUint16(offset, 1, true);
  offset += 2;
  view.setUint32(offset, sampleRate, true);
  offset += 4;
  view.setUint32(offset, sampleRate * 2, true);
  offset += 4;
  view.setUint16(offset, 2, true);
  offset += 2;
  view.setUint16(offset, 16, true);
  offset += 2;
  writeString('data');
  view.setUint32(offset, sampleCount * 2, true);
  offset += 4;

  let phase = 0;

  for (let sample = 0; sample < sampleCount; sample += 1) {
    const progress = sample / sampleCount;
    const currentFrequency = endFrequency
      ? frequency + (endFrequency - frequency) * progress
      : frequency;
    const envelope = Math.max(0, 1 - progress);
    const value = getWaveValue(type, phase) * volume * envelope;

    view.setInt16(offset, Math.max(-1, Math.min(1, value)) * 32767, true);
    offset += 2;
    phase += currentFrequency / sampleRate;
  }

  const bytes = new Uint8Array(buffer);
  let binary = '';

  for (let index = 0; index < bytes.length; index += 1) {
    binary += String.fromCharCode(bytes[index]);
  }

  return `data:audio/wav;base64,${window.btoa(binary)}`;
}

function getHtmlAudio(sound) {
  if (!htmlAudioCache.has(sound)) {
    const tone = SOUND_TONES[sound] ?? SOUND_TONES.laser;
    const audio = new Audio(encodeWavDataUrl(tone));

    audio.preload = 'auto';
    htmlAudioCache.set(sound, audio);
  }

  return htmlAudioCache.get(sound);
}

function playHtmlSound(sound) {
  const audio = getHtmlAudio(sound);

  audio.currentTime = 0;
  audio.play()?.catch?.(() => {});
}

function unlockHtmlAudio() {
  if (audioUnlocked) {
    return;
  }

  Object.keys(SOUND_TONES).forEach((sound) => {
    const audio = getHtmlAudio(sound);
    const previousVolume = audio.volume;

    audio.volume = 0.01;
    const playResult = audio.play();

    audio.volume = previousVolume;
    playResult?.then?.(() => {
      audio.pause();
      audio.currentTime = 0;
    })?.catch?.(() => {
      audio.volume = previousVolume;
    });
  });

  audioUnlocked = true;
}

function playTone({ frequency, endFrequency, duration, type = 'square', volume = 0.08 }) {
  if (shouldUseHtmlAudioFallback()) {
    return;
  }

  const context = getAudioContext();

  if (!context) {
    return;
  }

  const oscillator = context.createOscillator();
  const gain = context.createGain();
  const now = context.currentTime;
  const endTime = now + duration;

  oscillator.type = type;
  oscillator.frequency.setValueAtTime(frequency, now);

  if (endFrequency) {
    oscillator.frequency.exponentialRampToValueAtTime(endFrequency, endTime);
  }

  gain.gain.setValueAtTime(volume, now);
  gain.gain.exponentialRampToValueAtTime(0.001, endTime);

  oscillator.connect(gain);
  gain.connect(context.destination);
  oscillator.start(now);
  oscillator.stop(endTime);
}

function playSequence(tones) {
  tones.forEach((tone, index) => {
    window.setTimeout(() => {
      playTone(tone);
    }, index * 82);
  });
}

export function playArcadeSound(sound) {
  if (shouldUseHtmlAudioFallback()) {
    playHtmlSound(sound);
    return;
  }

  if (sound === 'laser') {
    playTone({
      frequency: 520,
      endFrequency: 390,
      duration: 0.055,
      type: 'triangle',
      volume: 0.035,
    });
  }

  if (sound === 'explosion') {
    playTone({
      frequency: 160,
      endFrequency: 48,
      duration: 0.22,
      type: 'sawtooth',
      volume: 0.1,
    });
  }

  if (sound === 'damage') {
    playTone({
      frequency: 220,
      endFrequency: 90,
      duration: 0.18,
      type: 'triangle',
      volume: 0.09,
    });
  }

  if (sound === 'bossIntro') {
    playSequence([
      { frequency: 130.81, duration: 0.12, type: 'sawtooth', volume: 0.055 },
      { frequency: 174.61, duration: 0.14, type: 'sawtooth', volume: 0.06 },
      { frequency: 261.63, duration: 0.18, type: 'triangle', volume: 0.065 },
    ]);
  }

  if (sound === 'bossHit') {
    playTone({
      frequency: 260,
      endFrequency: 180,
      duration: 0.07,
      type: 'triangle',
      volume: 0.045,
    });
  }

  if (sound === 'levelClear') {
    playSequence([
      { frequency: 440, duration: 0.09, type: 'triangle', volume: 0.07 },
      { frequency: 660, duration: 0.1, type: 'triangle', volume: 0.075 },
      { frequency: 880, duration: 0.13, type: 'triangle', volume: 0.08 },
    ]);
  }

  if (sound === 'powerup') {
    playTone({
      frequency: 720,
      endFrequency: 980,
      duration: 0.1,
      type: 'triangle',
      volume: 0.055,
    });
  }

  if (sound === 'victory') {
    playSequence([
      { frequency: 523.25, duration: 0.1, type: 'triangle', volume: 0.075 },
      { frequency: 659.25, duration: 0.1, type: 'triangle', volume: 0.075 },
      { frequency: 783.99, duration: 0.1, type: 'triangle', volume: 0.08 },
      { frequency: 1046.5, duration: 0.18, type: 'triangle', volume: 0.085 },
    ]);
  }
}
